import { useState, useRef } from 'react';
import { fetchWithAuth } from './authHelpers';

export const useLeaderboard = () => {
    const [leaderboard, setLeaderboard] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Use ref to track the current request
    const currentRequestRef = useRef(null);

    const fetchLeaderboardForEvent = async (event, leagueId = null) => {
        const requestId = Date.now() + Math.random(); // Unique request ID
        currentRequestRef.current = requestId;

        setLoading(true);
        setError(null);
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/events/${event.hltv_id}/fantasy-teams`, 'GET');

            // Check if this request is still the current one
            if (currentRequestRef.current !== requestId) {
                return; // Ignore this response as a newer request has been made
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch leaderboard');
            }

            const leaderboard = await response.json();

            // Check again before setting state
            if (currentRequestRef.current !== requestId) {
                return;
            }

            // Filter by league if leagueId is provided
            const filteredLeaderboard = leagueId
                ? leaderboard.filter(team => team.league?.toString() === leagueId.toString())
                : leaderboard;

            // Combine points for users with multiple teams
            const userPoints = {};
            filteredLeaderboard.forEach((team) => {
                const userId = team?.user?._id;
                if (!userId) {
                    console.error("Team does not have a user ID");
                    // Skip this team if user ID is missing
                    return;
                }
                if (!userPoints[userId]) {
                    userPoints[userId] = { ...team.user, points: 0, boostPoints: 0, cards: [] };
                }
                userPoints[userId].points += team.points;
                userPoints[userId].boostPoints += team.boostPoints;

                userPoints[userId].cards.push(...team.cards.map(card => ({
                    card: card,
                    points: card.points,
                    lastPoints: card.lastPoints,
                    boostPoints: card.boostPoints,
                    lastBoostPoints: card.lastBoostPoints,
                })));
            });

            // Convert the accumulated points back into an array and sort it
            const combinedLeaderboard = Object.values(userPoints).sort((a, b) => b.points - a.points);

            setLeaderboard(combinedLeaderboard);
        } catch (err) {
            // Only set error if this is still the current request
            setError(err.message);
            console.error("Error fetching leaderboard");
        } finally {
            // Only set loading to false if this is still the current request
            setLoading(false);
        }
    };

    const fetchLeaderboardForSeason = async (season) => {
        const requestId = Date.now() + Math.random(); // Unique request ID
        currentRequestRef.current = requestId;

        if (!season) {
            // Get the current season
            try {
                const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/seasons/current`, 'GET');

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to fetch current season');
                }

                const currentSeason = await response.json();
                season = currentSeason._id;
            } catch (err) {
                // Only set error if this is still the current request
                if (currentRequestRef.current === requestId) {
                    setError(err.message);
                    console.error("Error fetching current season");
                }
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/season/${season}/combined`, 'GET');

            // Check if this request is still the current one
            if (currentRequestRef.current !== requestId) {
                return; // Ignore this response as a newer request has been made
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch leaderboard');
            }

            const leaderboard = await response.json();

            // Check again before setting state
            if (currentRequestRef.current !== requestId) {
                return;
            }

            setLeaderboard(leaderboard);
        } catch (err) {
            setLoading(false);
            // Only set error if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
                setError(err.message);
                console.error("Error fetching leaderboard");
            }
        } finally {
            // Only set loading to false if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
            }
        }
    };

    const fetchLeaderboardForLeague = async (leagueId, season) => {
        const requestId = Date.now() + Math.random(); // Unique request ID
        currentRequestRef.current = requestId;

        if (!leagueId) {
            if (currentRequestRef.current === requestId) {
                setError('League ID is required');
            }
            return;
        }

        let seasonId = season;
        if (!seasonId) {
            // Get the current season
            try {
                const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/seasons/current`, 'GET');

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to fetch current season');
                }

                const currentSeason = await response.json();
                seasonId = currentSeason._id;
            } catch (err) {
                // Only set error if this is still the current request
                if (currentRequestRef.current === requestId) {
                    setError(err.message);
                    console.error("Error fetching current season");
                }
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/leaderboard/${seasonId}`, 'GET');

            // Check if this request is still the current one
            if (currentRequestRef.current !== requestId) {
                return; // Ignore this response as a newer request has been made
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch league leaderboard');
            }

            const leaderboard = await response.json();

            // Check again before setting state
            if (currentRequestRef.current !== requestId) {
                return;
            }

            setLeaderboard(leaderboard);
        } catch (err) {
            // Only set error if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
                setError(err.message);
                console.error("Error fetching league leaderboard");
            }
        } finally {
            // Only set loading to false if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
            }
        }
    };

    const fetchPremiumLeaderboardForSeason = async (season) => {
        const requestId = Date.now() + Math.random(); // Unique request ID
        currentRequestRef.current = requestId;

        if (!season) {
            // Get the current season
            try {
                const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/seasons/current`, 'GET');

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to fetch current season');
                }

                const currentSeason = await response.json();
                season = currentSeason._id;
            } catch (err) {
                // Only set error if this is still the current request
                if (currentRequestRef.current === requestId) {
                    setError(err.message);
                    console.error("Error fetching current season");
                }
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/season/${season}/combined?premium=true`, 'GET');

            // Check if this request is still the current one
            if (currentRequestRef.current !== requestId) {
                return; // Ignore this response as a newer request has been made
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch premium leaderboard');
            }

            const leaderboard = await response.json();

            // Check again before setting state
            if (currentRequestRef.current !== requestId) {
                return;
            }

            setLeaderboard(leaderboard);
        } catch (err) {
            // Only set error if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
                setError(err.message);
                console.error("Error fetching premium leaderboard");
            }
        } finally {
            // Only set loading to false if this is still the current request
            if (currentRequestRef.current === requestId) {
                setLoading(false);
            }
        }
    };



    // USE THIS TO HIGHLIGHT THE CURRENT USER IN THE LEADERBOARD
    const getUserId = async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/current`, 'GET');
            const data = await response.json();
            return data._id;
        } catch (error) {
            setError(error.message);
        }
    };

    // Function to clear leaderboard and cancel current request
    const clearLeaderboard = () => {
        currentRequestRef.current = null; // Cancel any pending requests
        setLeaderboard(null);
        setError(null);
        setLoading(false);
    };

    return { leaderboard, error, loading, fetchLeaderboardForEvent, fetchLeaderboardForSeason, fetchLeaderboardForLeague, fetchPremiumLeaderboardForSeason, getUserId, clearLeaderboard };
};

export default useLeaderboard;